import { useRef } from 'react';
import { getLogger } from '@/idv/utils';
const DEFAULT_THRESHOLD = {
  warning: 20,
  error: 50
};

/**
 * Renders only count toward the thresholds within a window this long: a render loop burns through
 * them in milliseconds, so the window is what separates one from a user sitting on a screen.
 */
const WINDOW_MS = 1_000;
type options = {
  threshold: {
    warning: number;
    error: number;
  };
};
const useRenderCountWarning = (componentName: string, options: options = {
  threshold: DEFAULT_THRESHOLD
}) => {
  const renderCount = useRef(0);
  const windowStartedAt = useRef(0);
  const hasWarned = useRef(false);
  const hasErrored = useRef(false);
  const {
    logWarn,
    logError
  } = getLogger({
    location: componentName
  });
  const now = Date.now();
  if (now - windowStartedAt.current > WINDOW_MS) {
    windowStartedAt.current = now;
    renderCount.current = 0;
  }
  renderCount.current += 1;
  if (renderCount.current > options.threshold.error && !hasErrored.current) {
    hasErrored.current = true;
    logError(`Excessive re-renders detected in "${componentName}" (${renderCount.current} in under ${WINDOW_MS}ms, threshold ${options.threshold.error}). This may cause performance issues or indicate an infinite render loop. Check for missing dependency arrays, unstable references, or state updates during render.`, undefined, {
      renderCount: renderCount.current,
      threshold: options.threshold.error,
      windowMs: WINDOW_MS
    });
  } else if (renderCount.current > options.threshold.warning && !hasWarned.current) {
    hasWarned.current = true;
    logWarn(`High render count in "${componentName}" (${renderCount.current} in under ${WINDOW_MS}ms, threshold ${options.threshold.warning}). Consider memoizing callbacks with useCallback, values with useMemo, or components with React.memo.`, undefined, {
      renderCount: renderCount.current,
      threshold: options.threshold.warning,
      windowMs: WINDOW_MS
    });
  }
};
export default useRenderCountWarning;